import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDownToDot, BellRing, Eye, Radio, Send, ShieldCheck, Shuffle, Zap } from "lucide-react";


const SOURCE_LABELS = {
    hook: "webhooks",
    api: "api",
    agent: "agents",
    monitor: "monitors",
    console: "console",
};

// Beeceptor-style method / protocol badge colors
const BADGE_CLS = {
    POST: "border-emerald-500/30 bg-emerald-950/60 text-emerald-400",
    INGEST: "border-cyan-500/30 bg-cyan-950/60 text-cyan-400",
    HMAC: "border-purple-500/30 bg-purple-950/60 text-purple-300",
    JSON: "border-cyan-500/30 bg-cyan-950/60 text-cyan-300",
    PUSH: "border-cyan-500/30 bg-cyan-950/60 text-cyan-400",
    POLL: "border-amber-500/30 bg-amber-950/60 text-amber-400",
    WATCH: "border-cyan-500/30 bg-cyan-950/60 text-cyan-300",
    ALERT: "border-amber-500/30 bg-amber-950/60 text-amber-300",
    STORE: "border-teal-500/30 bg-teal-950/60 text-teal-300",
    WS: "border-cyan-500/30 bg-cyan-950/60 text-cyan-300",
};

const CHIP = {
    ok: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300",
    warn: "border-amber-500/30 bg-amber-500/15 text-amber-300",
    off: "border-slate-700 bg-slate-800/50 text-slate-500",
};

const host = (u) => {
    try {
        return new URL(u).hostname;
    } catch {
        return String(u || "");
    }
};

// ---- pipeline definitions per app template (clickable stations) ----
function buildNodes(app, sources, perMin) {
    const t = app?.template;
    const cfg = app?.config || {};
    const srcLabel = sources.length ? sources.join(" · ") : "waiting for traffic";

    if (t === "webhook-receiver") {
        const targets = [
            ...(cfg.forward_url ? [cfg.forward_url] : []),
            ...(cfg.forward_targets || []).map((x) => x?.url).filter(Boolean),
        ];
        const hosts = [...new Set(targets.map(host))].filter(Boolean);
        const hasTemplates = (cfg.forward_targets || []).some((x) => x?.template);
        const nodes = [
            {
                id: "source", icon: ArrowDownToDot, badge: "POST", label: "Incoming", sub: srcLabel,
                chip: { text: "listening", cls: CHIP.ok },
                detail: `Events arrive here when a producer (Stripe, GitHub, your own service) POSTs to this app's public webhook URL — /api/hooks/${app?.hook_slug || "…"}. Every request is captured with its full payload and mirrored into the live stream below.`,
            },
            {
                id: "mdh", icon: ShieldCheck, badge: "HMAC", label: "MDH verifies", sub: cfg.stripe_secret ? "signature checks on" : "recording only",
                chip: cfg.stripe_secret ? { text: "verify on", cls: CHIP.ok } : { text: "verify off", cls: CHIP.off },
                detail: cfg.stripe_secret
                    ? "Each request is recorded and cryptographically verified against the signing secret you stored — forged or tampered requests get a 400 and never reach your destinations."
                    : "Each request is recorded and logged. Store the producer's signing secret (e.g. Stripe's whsec_…) in this app's config and MDH will reject forged requests with a 400.",
            },
        ];
        if (hasTemplates) {
            nodes.push({
                id: "transform", icon: Shuffle, badge: "JSON", label: "Transform", sub: "payloads reshaped",
                chip: { text: "templated", cls: CHIP.ok },
                detail: "Your routing templates reshape each payload per destination — one incoming webhook can fan out as a Slack message here, a database row there.",
            });
        }
        nodes.push({
            id: "dest", icon: Send, badge: "POST", label: "Forwarded to",
            sub: hosts.length ? hosts.slice(0, 2).join(" · ") + (hosts.length > 2 ? ` +${hosts.length - 2}` : "") : "no route set",
            chip: hosts.length ? { text: `${hosts.length} route${hosts.length > 1 ? "s" : ""}`, cls: CHIP.ok } : { text: "not configured", cls: CHIP.warn },
            detail: hosts.length
                ? `Every event is delivered to ${hosts.join(", ")}. Failed deliveries retry at 1s → 5s → 30s, then park for one-click replay.`
                : "Nothing set up yet — paste a URL into “Route inbound webhooks to” below, and every event you receive gets delivered there automatically.",
        });
        return nodes;
    }

    if (t === "outbound-webhooks") {
        return [
            {
                id: "source", icon: Zap, badge: "INGEST", label: "Published", sub: srcLabel,
                chip: { text: "accepting", cls: CHIP.ok },
                detail: "Events are published through the ingest API (or the console below) — anything your services emit: payment.ok, order.shipped, user.signup.",
            },
            {
                id: "mdh", icon: ShieldCheck, badge: "HMAC", label: "MDH signs + queues", sub: "hmac · pattern match",
                chip: { text: "signing", cls: CHIP.ok },
                detail: "Each event is matched against your subscribers' event patterns, signed with the endpoint's own secret (whsec_…), and queued for delivery.",
            },
            {
                id: "dest", icon: Send, badge: "POST", label: "Delivered to", sub: "subscriber endpoints",
                chip: { text: "retries armed", cls: CHIP.ok },
                detail: "Subscriber endpoints receive signed POSTs they can verify. Failures retry at 1s → 5s → 30s before parking for replay — the full attempt history is in the deliveries table below.",
            },
        ];
    }

    if (t) {
        // monitor-style templates: rest, cron, ssl, node, machine, docker, log, ssh
        const pollDriven = t === "rest-api-monitor" || t === "ssl-cert-monitor";
        return [
            {
                id: "source", icon: Eye, badge: pollDriven ? "POLL" : "PUSH", label: "Your systems", sub: srcLabel,
                chip: { text: "watching", cls: CHIP.ok },
                detail: "Signals arrive from whatever this app watches — uptime polls, agent heartbeats, cron pings, log tails, SSH sessions.",
            },
            {
                id: "mdh", icon: Radio, badge: "WATCH", label: "MDH watches", sub: `${perMin}/min flowing`,
                chip: { text: "live", cls: CHIP.ok },
                detail: "Every check and heartbeat streams in live. State flips — down/recovered, missed/back, expiring — become events the moment they happen.",
            },
            {
                id: "alerts", icon: BellRing, badge: "ALERT", label: "Alerts", sub: "stream · sms",
                chip: { text: "armed", cls: CHIP.ok },
                detail: "Flips land in the live stream below, and text your phone if SMS alerts are enabled in Settings → Profile.",
            },
        ];
    }

    return [
        {
            id: "source", icon: Zap, badge: "INGEST", label: "Events in", sub: srcLabel,
            chip: { text: "accepting", cls: CHIP.ok },
            detail: "Events arrive via the ingest API or the console below — anything you want to track.",
        },
        {
            id: "mdh", icon: Radio, badge: "STORE", label: "MDH records", sub: `${perMin}/min flowing`,
            chip: { text: "live", cls: CHIP.ok },
            detail: "Every event is stored with its full payload and mirrored into the live stream in real time.",
        },
        {
            id: "dest", icon: Send, badge: "WS", label: "Onward", sub: "live stream",
            chip: { text: "streaming", cls: CHIP.ok },
            detail: "Watch everything land in the stream below. Want delivery to endpoints? Re-create this app from the Outbound Webhooks template.",
        },
    ];
}

const Connector = ({ testid, pulse }) => (
    <div className="relative hidden min-w-8 flex-1 self-center sm:block">
        <svg className="h-4 w-full" preserveAspectRatio="none" aria-hidden="true">
            <line x1="0" y1="50%" x2="100%" y2="50%" stroke="rgba(34,211,238,0.15)" strokeWidth="1.5" />
            <line
                data-testid={testid}
                x1="0" y1="50%" x2="100%" y2="50%"
                stroke="rgba(34,211,238,0.6)" strokeWidth="1.5"
                strokeDasharray="4 10" strokeLinecap="round"
                className="animate-dash-flow"
            />
        </svg>
        <AnimatePresence>
            {pulse && (
                <motion.span
                    key={pulse.id}
                    data-testid="flow-pulse"
                    initial={{ left: "2%", opacity: 0.95 }}
                    animate={{ left: "97%", opacity: 0.15 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.85, ease: "easeIn" }}
                    className={`absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full ${
                        pulse.ok
                            ? "bg-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.95)]"
                            : "bg-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.95)]"
                    }`}
                />
            )}
        </AnimatePresence>
    </div>
);

const MethodBadge = ({ kind }) => (
    <span
        className={`rounded border px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-wider ${BADGE_CLS[kind] || BADGE_CLS.POST}`}
    >
        {kind}
    </span>
);

export default function FlowVisualizer({ app, events, live }) {
    const [pulse, setPulse] = useState(null);
    const lastId = useRef(null);

    const sources = useMemo(
        () => [
            ...new Set(
                (events || [])
                    .map((e) =>
                        SOURCE_LABELS[e.source] ||
                        (e.simulated ? "simulator"
                            : /^(http\.|ssl\.|monitor\.|cron\.)/.test(e.type || "") ? "monitors"
                            : "events"),
                    )
                    .filter(Boolean),
            ),
        ].slice(0, 3),
        [events],
    );
    const perMin = (events || []).filter(
        (e) => Date.now() - new Date(e.created_at).getTime() < 60000,
    ).length;

    const nodes = useMemo(() => buildNodes(app, sources, perMin), [app, sources, perMin]);
    const [active, setActive] = useState(null);
    const activeNode = nodes.find((n) => n.id === active) || nodes[1] || nodes[0];

    // reset selection when switching apps/templates
    useEffect(() => setActive(null), [app?.app_id, app?.template]);

    // a new event at the head of the buffer → pulse down the pipeline
    useEffect(() => {
        const e = events?.[0];
        if (!e || e.id === lastId.current) return;
        lastId.current = e.id;
        const p = { id: `${e.id}-${Date.now()}`, ok: (e.status ?? 200) < 400 };
        setPulse(p);
        const t = setTimeout(() => setPulse((cur) => (cur?.id === p.id ? null : cur)), 1100);
        return () => clearTimeout(t);
    }, [events]);

    return (
        <div data-testid="flow-visualizer" className="glass-panel rounded-xl px-5 py-4">
            {/* header eyebrow strip */}
            <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    how events flow through this app — click a stage
                </div>
                <div className="flex items-center gap-2">
                    <span
                        data-testid="flow-throughput"
                        className="rounded border border-slate-800 bg-slate-900/60 px-2 py-0.5 font-mono text-[9.5px] text-slate-400"
                    >
                        {perMin}/min flowing
                    </span>
                    <span
                        data-testid="flow-live-pill"
                        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-widest ${
                            live
                                ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
                                : "border-slate-700 bg-slate-800/50 text-slate-500"
                        }`}
                    >
                        <span className={`h-1.5 w-1.5 rounded-full ${live ? "animate-pulse bg-emerald-400" : "bg-slate-600"}`} />
                        {live ? "live" : "offline"}
                    </span>
                </div>
            </div>

            {/* station graph */}
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                {nodes.map((n, i) => (
                    <div key={n.id} className="contents">
                        <button
                            data-testid={`flow-node-${n.id}`}
                            onClick={() => setActive(n.id)}
                            className={`flex-1 rounded-lg border px-3 py-2.5 text-left transition-all duration-200 ease-out ${
                                activeNode.id === n.id
                                    ? "border-cyan-400/60 bg-cyan-950/40 shadow-[0_0_15px_rgba(6,182,212,0.25)]"
                                    : "border-slate-800 bg-slate-900/50 hover:border-slate-700 hover:bg-slate-800/50"
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-2">
                                    <n.icon
                                        className={`h-3.5 w-3.5 ${
                                            activeNode.id === n.id ? "text-cyan-300" : "text-slate-500"
                                        }`}
                                    />
                                    <span className="font-mono text-[11px] font-semibold text-slate-200">
                                        {n.label}
                                    </span>
                                </span>
                                <MethodBadge kind={n.badge} />
                            </div>
                            <div className="mt-1 truncate font-mono text-[10px] text-slate-500">
                                {n.sub}
                            </div>
                        </button>
                        {i < nodes.length - 1 && (
                            <Connector
                                testid={i === 0 ? "flow-beam-in" : i === nodes.length - 2 ? "flow-beam-out" : `flow-beam-mid-${i}`}
                                pulse={pulse && (i === 0 || (pulse.ok && i === nodes.length - 2)) ? pulse : null}
                            />
                        )}
                    </div>
                ))}
            </div>

            {/* inspector detail panel */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={`${app?.app_id}-${activeNode.id}`}
                    data-testid="flow-node-detail"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    className="mt-3.5 rounded-lg border border-slate-800 bg-slate-950/60 p-4"
                >
                    <div className="mb-1.5 flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-cyan-400">
                            {activeNode.label}
                        </span>
                        <MethodBadge kind={activeNode.badge} />
                        {activeNode.chip && (
                            <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] ${activeNode.chip.cls}`}>
                                {activeNode.chip.text}
                            </span>
                        )}
                    </div>
                    <p className="font-mono text-xs leading-relaxed text-slate-400">
                        {activeNode.detail}
                    </p>
                </motion.div>
            </AnimatePresence>
        </div>
    );
}
